import { useEffect, useState } from "react";
import api from "../../utils/axios";
import { useNavigate } from "react-router-dom";
import { Fragment } from "react";

const AdminDashboard = () => {
  const [donations, setDonations] = useState([]);
  const [txnIds, setTxnIds] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NONE");
  const [view, setView] = useState("DONATIONS");
  const [donors, setDonors] = useState([]);
  const [expandedDonor, setExpandedDonor] = useState(null);

  // Volunteer modal states
  const [showVolunteerModal, setShowVolunteerModal] = useState(false);
  const [vName, setVName] = useState("");
  const [vMobile, setVMobile] = useState("");
  const [vPassword, setVPassword] = useState("");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const name = localStorage.getItem("name");

  // Fetch all donations
  const fetchDonations = async () => {
    try {
      const res = await api.get(
        "/admin/donations/all",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setDonations(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load donations");
    }
  };

  useEffect(() => {
    fetchDonations();
  }, []);

  // Approve donation
  const approveDonation = async (donationId) => {
    if (!txnIds[donationId]) return alert("Please enter transaction ID");

    try {
      await api.post(
        `/verify/approve/${donationId}`,
        { transaction_id: txnIds[donationId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Donation approved");
      fetchDonations();
    } catch (err) {
      console.error(err);
      alert("Approval failed");
    }
  };

  // fetch donors
  const fetchDonors = async () => {
    try {
      const res = await api.get(
        "/admin/donors/all",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDonors(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load donors");
    }
  };

  // Add volunteer
  const addVolunteer = async () => {
    if (!vName || !vMobile || !vPassword) return alert("All fields required");

    try {
      await api.post(
        "/admin/users/add-volunteer",
        { name: vName, mobile: vMobile, password: vPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Volunteer added successfully");
      setVName("");
      setVMobile("");
      setVPassword("");
      setShowVolunteerModal(false);
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to add volunteer");
    }
  };

  // Logout
  const logout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };

  // Filter + sort logic
  const processedDonations = donations
    .filter((d) => {
      const text = search.toLowerCase();
      const nameMatch = d.donor?.name?.toLowerCase().includes(text);
      const mobileMatch = d.donor?.mobile?.includes(text);
      const statusMatch = statusFilter === "ALL" || d.status === statusFilter;
      return (nameMatch || mobileMatch) && statusMatch;
    })
    .sort((a, b) => {
      if (sortOrder === "LOW") return a.amount - b.amount;
      if (sortOrder === "HIGH") return b.amount - a.amount;
      return 0;
    });

  return (
    <div className="min-h-screen bg-rose-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              IGF Admin Control Panel
            </h1>
            <p className="text-gray-500 text-sm">
              Welcome back, {name || "Admin"}
            </p>
          </div>
          <button
            onClick={logout}
            className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg shadow-sm hover:shadow-md transition"
          >
            Logout
          </button>
        </div>

        {/* ADD VOLUNTEER BUTTON */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowVolunteerModal(true)}
            className="bg-rose-600 hover:bg-rose-700 text-white px-5 py-2 rounded-lg shadow-sm hover:shadow-md transition"
          >
            + Add Volunteer
          </button>
        </div>

	  {/*toggle */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => {
              setView("DONORS");
              fetchDonors();
            }}
            className={`px-4 py-2 rounded-lg ${view === "DONORS"
              ? "bg-rose-600 text-white"
              : "bg-gray-200"
              }`}
          >
            Donors List
          </button>

          <button
            onClick={() => {
              setView("DONATIONS");
              fetchDonations();
            }}
            className={`px-4 py-2 rounded-lg ${view === "DONATIONS"
              ? "bg-rose-600 text-white"
              : "bg-gray-200"
              }`}
          >
            Donations List
          </button>
        </div>

        {/* FILTER + SEARCH */}
        <div className="bg-white p-4 rounded-xl shadow mb-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <input
            type="text"
            placeholder="Search by donor name or mobile..."
            className="border p-3 rounded-lg w-full md:w-1/3 focus:ring-2 focus:ring-blue-400 outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex gap-3">
            <select
              className="border p-3 rounded-lg"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
            </select>
            <select
              className="border p-3 rounded-lg"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="NONE">Sort by Amount</option>
              <option value="LOW">Low → High</option>
              <option value="HIGH">High → Low</option>
            </select>
          </div>
        </div>

       {view === "DONATIONS" && (
          <>
            {/* DONATIONS TABLE */}
            <div className="overflow-x-auto bg-white rounded-xl shadow">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-rose-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">
                      Donor Name
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">
                      Mobile
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {processedDonations.map((d) => (
                    <tr key={d._id} className="hover:bg-rose-50 transition">
                      <td className="px-6 py-4 text-sm">{d.donor?.name || "Unknown"}</td>
                      <td className="px-6 py-4 text-sm">{d.donor?.phone}</td>
                      <td className="px-6 py-4 text-sm">₹{d.amount}</td>
                      <td className="px-6 py-4 text-sm">
                        {d.createdAt ? new Date(d.createdAt).toLocaleString() : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${d.status === "APPROVED"
                            ? "bg-green-100 text-green-700"
                            : "bg-orange-100 text-orange-700"
                            }`}
                        >
                          {d.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {d.status === "PENDING" ? (
                          <div className="flex flex-col md:flex-row gap-2 min-w-[150px]">
                            <input
                              type="text"
                              placeholder="Txn ID"
                              className="border p-2 rounded-lg flex-1 min-w-[120px]"
                              value={txnIds[d._id] || ""}
                              onChange={(e) =>
                                setTxnIds({ ...txnIds, [d._id]: e.target.value })
                              }
                            />
                            <button
                              onClick={() => approveDonation(d._id)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg min-w-[80px]"
                            >
                              Approve
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-500">{d.transaction_id || "-"}</span>
                        )}
                      </td>

                    </tr>
                  ))}
                  {processedDonations.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center py-6 text-gray-400">
                        No donations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {view === "DONORS" && (
  <div className="space-y-6">

    {/* 🎂 BIRTHDAYS THIS MONTH */}
    <div className="bg-white rounded-xl shadow p-4">
      <h3 className="font-semibold text-gray-800 mb-2">
        🎂 Birthdays This Month
      </h3>

      {donors.filter(d => {
        if (!d.dob) return false;
        const dob = new Date(d.dob);
        return dob.getMonth() === new Date().getMonth();
      }).length === 0 ? (
        <p className="text-gray-400 text-sm">No birthdays this month</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {donors
            .filter(d => d.dob && new Date(d.dob).getMonth() === new Date().getMonth())
            .map(d => (
              <div
                key={d._id}
                className="bg-rose-50 px-3 py-2 rounded-lg text-sm border"
              >
                <span className="font-medium">{d.name}</span>
                <span className="text-gray-500 ml-2">
                  {new Date(d.dob).getDate()}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>

    {/* 📊 DONORS TABLE */}
    <div className="bg-white rounded-2xl shadow-lg overflow-x-auto border border-rose-100">
      <table className="min-w-[1000px] w-full text-sm">
        <thead className="bg-rose-50 text-gray-700 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 text-left">Donor</th>

            {/* MONTH HEADERS */}
            {[
              "Jan","Feb","Mar","Apr","May","Jun",
              "Jul","Aug","Sep","Oct","Nov","Dec"
            ].map((m) => (
              <th key={m} className="px-3 py-3 text-center">{m}</th>
            ))}

            <th className="px-4 py-3 text-center">Total</th>
          </tr>
        </thead>

        <tbody className="divide-y">
          {donors.map((donor) => {

            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth();

            // 🧠 Monthly aggregation
            const monthlyTotals = Array(12).fill(0);

            donor.donations.forEach((d) => {
              const date = new Date(d.createdAt);
              if (date.getFullYear() === currentYear) {
                monthlyTotals[date.getMonth()] += d.amount;
              }
            });

            return (
              <Fragment key={donor._id}>
                <tr
                  className="hover:bg-rose-50 cursor-pointer"
                  onClick={() =>
                    setExpandedDonor(
                      expandedDonor === donor._id ? null : donor._id
                    )
                  }
                >
                  {/* NAME */}
                  <td className="px-4 py-3">
                    <div className="font-medium">{donor.name}</div>
                    <div className="text-xs text-gray-400">
                      {donor.phone}
                    </div>
                  </td>

                  {/* MONTHLY CELLS */}
                  {monthlyTotals.map((amt, i) => {
                    let display;

                    if (i > currentMonth) display = "Upcoming";
                    else if (amt === 0) display = "NA";
                    else display = `₹${amt}`;

                    return (
                      <td key={i} className="px-3 py-3 text-center text-xs">
                        {display}
                      </td>
                    );
                  })}

                  {/* TOTAL */}
                  <td className="px-4 py-3 text-center font-semibold text-green-700">
                    ₹{donor.total_donated || 0}
                  </td>
                </tr>

                {/* 🔽 EXPANDED DETAILS */}
                {expandedDonor === donor._id && (
                  <tr>
                    <td colSpan="14" className="bg-gray-50 px-6 py-5">
                      <div className="grid md:grid-cols-2 gap-4 text-sm">

                        <div>
                          <p><b>Name:</b> {donor.name}</p>
                          <p><b>Phone:</b> {donor.phone}</p>
                          <p><b>Pincode:</b> {donor.pincode}</p>
                        </div>

                        <div>
                          <p>
                            <b>DOB:</b>{" "}
                            {donor.dob
                              ? new Date(donor.dob).toLocaleDateString()
                              : "-"}
                          </p>
                          <p><b>Address:</b> {donor.address || "-"}</p>
                          <p><b>Total Donations:</b> ₹{donor.total_donated}</p>
                        </div>

                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
)}
      </div>

      {/* ADD VOLUNTEER MODAL */}
      {showVolunteerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Add Volunteer
            </h2>
            <input
              className="border p-3 rounded-lg w-full mb-3"
              placeholder="Name"
              value={vName}
              onChange={(e) => setVName(e.target.value)}
            />
            <input
              className="border p-3 rounded-lg w-full mb-3"
              placeholder="Mobile"
              value={vMobile}
              onChange={(e) => setVMobile(e.target.value)}
            />
            <input
              className="border p-3 rounded-lg w-full mb-3"
              placeholder="Temporary Password"
              type="password"
              value={vPassword}
              onChange={(e) => setVPassword(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                className="px-4 py-2 bg-gray-300 rounded-lg"
                onClick={() => setShowVolunteerModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-rose-600 text-white rounded-lg"
                onClick={addVolunteer}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
